import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {useAuth} from "../authentication/AuthContext";
import {useCompleteSession} from "./hooks/useSession";
import {
    POMODORO_STATE_STORAGE_KEY,
    readPersistedTimerState,
    STOPWATCH_STATE_STORAGE_KEY,
    TIMER_TYPE_STORAGE_KEY,
} from './timerStorage';

const sessionEndAudio = new Audio('/sounds/endsession-v1.mp3');

function playSessionEndAudio() {
    void sessionEndAudio.play().catch(() => {
    });
}

function readTimerType() {
    try {
        const timerType = localStorage.getItem(TIMER_TYPE_STORAGE_KEY);
        return timerType === 'stopwatch' ? 'stopwatch' : 'pomodoro';
    } catch (error) {
        return 'pomodoro';
    }
}

function readWidgetSnapshot(now = Date.now()) {
    const timerType = readTimerType();

    if (timerType === 'stopwatch') {
        const stopwatchState = readPersistedTimerState(STOPWATCH_STATE_STORAGE_KEY);
        if (!stopwatchState) {
            return null;
        }

        const elapsedBase = Number.isFinite(stopwatchState.elapsedSeconds) && stopwatchState.elapsedSeconds >= 0
            ? Math.trunc(stopwatchState.elapsedSeconds)
            : 0;
        const persistedAt = Number(stopwatchState.persistedAt);
        const elapsedDrift = stopwatchState.isRunning && Number.isFinite(persistedAt)
            ? Math.max(0, Math.floor((now - persistedAt) / 1000))
            : 0;
        const elapsedSeconds = elapsedBase + elapsedDrift;
        const isActive = Boolean(stopwatchState.isRunning) || elapsedSeconds > 0 || !Boolean(stopwatchState.isStart);

        if (!isActive) {
            return null;
        }

        return {
            timerType: 'stopwatch',
            isRunning: Boolean(stopwatchState.isRunning),
            elapsedSeconds,
            rawState: stopwatchState,
        };
    }

    const pomodoroState = readPersistedTimerState(POMODORO_STATE_STORAGE_KEY);
    if (!pomodoroState) {
        return null;
    }

    const phase = ['focus', 'break', 'idle', 'completed'].includes(pomodoroState.phase) ? pomodoroState.phase : 'idle';
    const isTimerPhase = phase === 'focus' || phase === 'break';
    const isRunning = Boolean(pomodoroState.isRunning);
    const remainingFromPause = Number.isFinite(pomodoroState.remainingSeconds) && pomodoroState.remainingSeconds >= 0
        ? Math.trunc(pomodoroState.remainingSeconds)
        : 0;
    const remainingFromTarget = Math.ceil(((Number(pomodoroState.targetEndAt) || 0) - now) / 1000);
    const remainingSeconds = isRunning ? Math.max(0, remainingFromTarget) : remainingFromPause;
    const isActive = isTimerPhase && (isRunning || remainingSeconds > 0);

    if (!isActive) {
        return null;
    }

    return {
        timerType: 'pomodoro',
        phase,
        isRunning,
        remainingSeconds,
        rawState: pomodoroState,
    };
}

function formatDuration(totalSeconds) {
    const safeSeconds = Math.max(0, Math.trunc(totalSeconds || 0));
    const hours = Math.floor(safeSeconds / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    const seconds = safeSeconds % 60;

    if (hours > 0) {
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }

    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function WorkSessionTimerWidget() {
    const navigate = useNavigate();
    const location = useLocation();
    const {authToken} = useAuth();
    const {mutateAsync: completeSession} = useCompleteSession(authToken);
    const [snapshot, setSnapshot] = useState(() => readWidgetSnapshot());
    const isWorkSessionPage = location.pathname === '/session';

    const refreshSnapshot = useCallback(() => {
        setSnapshot(readWidgetSnapshot());
    }, []);

    useEffect(() => {
        const intervalId = window.setInterval(() => {
            refreshSnapshot();
        }, 1000);

        const onStorage = () => {
            refreshSnapshot();
        };
        window.addEventListener('storage', onStorage);

        return () => {
            window.clearInterval(intervalId);
            window.removeEventListener('storage', onStorage);
        };
    }, [refreshSnapshot]);

    useEffect(() => {
        if (isWorkSessionPage || !snapshot || snapshot.timerType !== 'pomodoro' || !snapshot.isRunning || snapshot.remainingSeconds > 0) {
            return;
        }

        const persistedState = readPersistedTimerState(POMODORO_STATE_STORAGE_KEY);
        if (!persistedState || !persistedState.isRunning) {
            refreshSnapshot();
            return;
        }

        const expiredBaseState = {
            ...persistedState,
            isRunning: false,
            remainingSeconds: 0,
            targetEndAt: null,
            persistedAt: Date.now(),
            completionError: '',
        };

        if (persistedState.phase === 'break') {
            localStorage.setItem(POMODORO_STATE_STORAGE_KEY, JSON.stringify({
                ...expiredBaseState,
                phase: 'idle',
            }));
            refreshSnapshot();
            return;
        }

        if (persistedState.phase !== 'focus') {
            localStorage.setItem(POMODORO_STATE_STORAGE_KEY, JSON.stringify(expiredBaseState));
            refreshSnapshot();
            return;
        }

        playSessionEndAudio();
        localStorage.setItem(POMODORO_STATE_STORAGE_KEY, JSON.stringify(expiredBaseState));

        const completedSessionId = persistedState.currentSessionId ?? null;
        if (!completedSessionId) {
            localStorage.setItem(POMODORO_STATE_STORAGE_KEY, JSON.stringify({
                ...expiredBaseState,
                completionError: 'Session finished locally but no session id was available to complete it.',
            }));
            refreshSnapshot();
            return;
        }

        void completeSession(completedSessionId)
            .then(() => {
                localStorage.setItem(POMODORO_STATE_STORAGE_KEY, JSON.stringify({
                    ...expiredBaseState,
                    phase: 'completed',
                    completedRounds: Math.max(0, Math.trunc(Number(persistedState.completedRounds) || 0) + 1),
                    currentSessionId: null,
                    completionError: '',
                }));
            })
            .catch((error) => {
                localStorage.setItem(POMODORO_STATE_STORAGE_KEY, JSON.stringify({
                    ...expiredBaseState,
                    phase: 'focus',
                    completionError: error instanceof Error ? error.message : 'Failed to complete session',
                }));
            })
            .finally(() => {
                refreshSnapshot();
            });
    }, [completeSession, isWorkSessionPage, refreshSnapshot, snapshot]);

    const handlePause = () => {
        if (!snapshot) {
            return;
        }

        if (snapshot.timerType === 'pomodoro') {
            const targetEndAt = Number(snapshot.rawState?.targetEndAt) || 0;
            const remainingSeconds = Math.max(0, Math.ceil((targetEndAt - Date.now()) / 1000));
            const nextState = {
                ...snapshot.rawState,
                isRunning: false,
                remainingSeconds,
                targetEndAt: null,
                persistedAt: Date.now(),
            };
            localStorage.setItem(POMODORO_STATE_STORAGE_KEY, JSON.stringify(nextState));
        } else {
            const persistedAt = Number(snapshot.rawState?.persistedAt);
            const drift = Number.isFinite(persistedAt) ? Math.max(0, Math.floor((Date.now() - persistedAt) / 1000)) : 0;
            const nextState = {
                ...snapshot.rawState,
                isRunning: false,
                elapsedSeconds: Math.max(0, Math.trunc(Number(snapshot.rawState?.elapsedSeconds) || 0) + drift),
                persistedAt: Date.now(),
            };
            localStorage.setItem(STOPWATCH_STATE_STORAGE_KEY, JSON.stringify(nextState));
        }

        refreshSnapshot();
    };

    const handleResume = () => {
        if (!snapshot) {
            return;
        }

        if (snapshot.timerType === 'pomodoro') {
            if (!(snapshot.phase === 'focus' || snapshot.phase === 'break') || snapshot.remainingSeconds <= 0) {
                return;
            }
            const nextState = {
                ...snapshot.rawState,
                isRunning: true,
                targetEndAt: Date.now() + (snapshot.remainingSeconds * 1000),
                remainingSeconds: null,
                persistedAt: Date.now(),
            };
            localStorage.setItem(POMODORO_STATE_STORAGE_KEY, JSON.stringify(nextState));
        } else {
            const nextState = {
                ...snapshot.rawState,
                isRunning: true,
                isStart: false,
                persistedAt: Date.now(),
            };
            localStorage.setItem(STOPWATCH_STATE_STORAGE_KEY, JSON.stringify(nextState));
        }

        refreshSnapshot();
    };

    const handleStop = () => {
        if (!snapshot) {
            return;
        }

        if (snapshot.timerType === 'pomodoro') {
            const nextState = {
                ...snapshot.rawState,
                phase: 'idle',
                isRunning: false,
                remainingSeconds: 0,
                targetEndAt: null,
                completedRounds: 0,
                completionError: '',
                currentSessionId: null,
                persistedAt: Date.now(),
            };
            localStorage.setItem(POMODORO_STATE_STORAGE_KEY, JSON.stringify(nextState));
        } else {
            const nextState = {
                ...snapshot.rawState,
                isRunning: false,
                isStart: true,
                elapsedSeconds: 0,
                currentSessionId: null,
                persistedAt: Date.now(),
            };
            localStorage.setItem(STOPWATCH_STATE_STORAGE_KEY, JSON.stringify(nextState));
        }

        refreshSnapshot();
    };

    const modeLabel = useMemo(() => {
        if (!snapshot) {
            return '';
        }
        if (snapshot.timerType === 'stopwatch') {
            return 'Stopwatch';
        }
        return snapshot.phase === 'break' ? 'Break' : 'Pomodoro';
    }, [snapshot]);

    if (!snapshot) {
        return null;
    }

    const displayValue = snapshot.timerType === 'pomodoro'
        ? formatDuration(snapshot.remainingSeconds)
        : formatDuration(snapshot.elapsedSeconds);

    return (
        <div className="fixed bottom-4 right-4 z-[1400] w-[230px] rounded-xl border border-indigo-400/25 bg-[#1D222D]/95 p-3 text-gray-100 shadow-[0_12px_30px_rgba(0,0,0,0.45)] backdrop-blur">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-[11px] uppercase tracking-wide text-indigo-300/90">{modeLabel}</p>
                    <p className="text-2xl font-light leading-none tracking-tight" style={{fontVariantNumeric: 'tabular-nums'}}>
                        {displayValue}
                    </p>
                    <p className="mt-1 text-[11px] text-gray-400">{snapshot.isRunning ? 'Running' : 'Paused'}</p>
                </div>
                <button
                    type="button"
                    className="rounded-md border border-gray-600 px-2 py-1 text-[11px] text-gray-200 hover:bg-gray-700/70"
                    onClick={() => navigate('/session')}
                >
                    Open
                </button>
            </div>
            {isWorkSessionPage ? (
                <p className="mt-3 text-[11px] text-gray-400">Use main timer controls on this page.</p>
            ) : (
                <div className="mt-3 flex gap-2">
                    {snapshot.isRunning ? (
                        <button
                            type="button"
                            className="flex-1 rounded-md bg-gray-700 px-2 py-1.5 text-xs font-medium hover:bg-gray-600"
                            onClick={handlePause}
                        >
                            Pause
                        </button>
                    ) : (
                        <button
                            type="button"
                            className="flex-1 rounded-md bg-indigo-600 px-2 py-1.5 text-xs font-medium hover:bg-indigo-500"
                            onClick={handleResume}
                        >
                            Resume
                        </button>
                    )}
                    <button
                        type="button"
                        className="flex-1 rounded-md bg-rose-700/80 px-2 py-1.5 text-xs font-medium hover:bg-rose-600/90"
                        onClick={handleStop}
                    >
                        Stop
                    </button>
                </div>
            )}
        </div>
    );
}

export default WorkSessionTimerWidget;
